import type { StudioBinding, StudioDataset, StudioDimension } from 'src/components/star-grid/reportStudio.types';
import { normalizeDimension } from 'src/components/star-grid/utils/studioApplySpec';
import type { PnlDimensionGroup, PnlDimensionHierarchy } from 'src/components/star-grid/pnlDimension.types';

const existingBinding = (bindings: readonly StudioBinding[], dimension: string, hierarchy: PnlDimensionHierarchy) => {
  if (hierarchy.binding_id) return bindings.find((binding) => binding.id === hierarchy.binding_id);
  const matches = bindings.filter(
    (binding) =>
      (binding.tree_id ?? binding.id) === hierarchy.hierarchy_id && [dimension, hierarchy.hierarchy_id?.split('::')[0]].includes(binding.dimension)
  );
  return matches.find((binding) => binding.hierarchy === (hierarchy.hierarchy ?? hierarchy.cdp_hierarchy)) ?? matches[0];
};

const discoveredBinding = (bindings: readonly StudioBinding[], dimension: string, hierarchy: PnlDimensionHierarchy): StudioBinding | undefined => {
  const id = hierarchy.binding_id ?? hierarchy.hierarchy_id;
  if (!id) return undefined;
  const existing = existingBinding(bindings, dimension, hierarchy);
  if (existing) return existing;
  const name = hierarchy.hierarchy ?? hierarchy.cdp_hierarchy ?? hierarchy.hierarchy_id?.split('::')[1] ?? hierarchy.label ?? id;
  return {
    id,
    label: hierarchy.label ?? dimension,
    dimension,
    hierarchy: name,
    tree_id: hierarchy.hierarchy_id ?? null,
    rollup: { dimension, hierarchy: name },
    subsets: [],
    attributes: []
  };
};
const dimensionBindingIds = (bindings: StudioBinding[], group: PnlDimensionGroup): readonly string[] => [
  ...new Set(
    group.hierarchies.flatMap((hierarchy) => {
      const binding = discoveredBinding(bindings, group.dimension, hierarchy);
      if (!binding) return [];
      if (!bindings.some((existing) => existing.id === binding.id)) bindings.push(binding);
      return [binding.id];
    })
  )
];

/** The dimensions a binding reads: its own, and the one its tree is a hierarchy of. */
const closureDimensions = (binding: StudioBinding): string[] =>
  [binding.dimension, binding.tree_id?.split('::')[0] ?? ''].filter(Boolean).map(normalizeDimension);

/**
 * An onboarded dataset can serve only the dimensions its own bindings read. A P&L hierarchy of one of
 * those is selectable (the service rolls up through any hierarchy of a bound dimension); any other P&L
 * dimension stays listed but unselectable, so the palette shows every dimension without letting one the
 * dataset cannot serve onto a shelf.
 */
const datasetSelectableIds = (dataset: StudioDataset, bindings: readonly StudioBinding[]): string[] => {
  const own = new Set(dataset.bindings.map((binding) => binding.id));
  // Only a binding with a tree can reroute a P&L hierarchy; a flat column has no closure to roll up through.
  const bound = new Set(dataset.bindings.filter((binding) => binding.tree_id).flatMap(closureDimensions));
  const discovered = bindings.filter((binding) => !own.has(binding.id) && closureDimensions(binding).some((dimension) => bound.has(dimension)));
  return [...(dataset.selectable_binding_ids ?? [...own]), ...discovered.map((binding) => binding.id)];
};

/** The dataset's own columns no P&L group lists (a flat column, or one named unlike any P&L dimension), one entry each. */
const uncoveredDimensions = (dataset: StudioDataset, dimensions: readonly StudioDimension[]): StudioDimension[] => {
  const covered = new Set(dimensions.flatMap((dimension) => dimension.binding_ids));
  return dataset.bindings
    .filter((binding) => !covered.has(binding.id))
    .map((binding) => ({ id: binding.id, label: binding.label, binding_ids: [binding.id] }));
};

export const withDiscoveredStudioBindings = (dataset: StudioDataset, groups: readonly PnlDimensionGroup[]): StudioDataset => {
  const bindings = [...dataset.bindings];
  const dimensions = groups.map((group) => ({ id: group.dimension, label: group.dimension, binding_ids: dimensionBindingIds(bindings, group) }));
  const onboarded = dataset.source === 'dataset';
  return {
    ...dataset,
    dimensions: onboarded ? [...dimensions, ...uncoveredDimensions(dataset, dimensions)] : dimensions,
    bindings: bindings.length === dataset.bindings.length ? dataset.bindings : bindings,
    selectable_binding_ids: onboarded ? datasetSelectableIds(dataset, bindings) : bindings.map((binding) => binding.id)
  };
};
